import { CarbonUser } from "./auth";
import {
  CERTIFIED_METRICS,
  getFleetViewMetrics,
  getMetricName,
  isFleetViewMetric,
  METRIC_CROP,
} from "./certifiedMetrics";
import { ConfigNode } from "protos/config/api/config_service";
import { CycleSlot, Metric, MetricValidation } from "./metrics";
import { DailyMetricResponse } from "protos/portal/metrics";
import { DateTime } from "luxon";
import {
  excelFormatter,
  getRenderDateTime,
} from "portal/components/CarbonDataGrid";
import { FleetView, ViewMode, viewModeFromJSON } from "protos/portal/users";
import {
  formatImplementationStatus,
  ImplementationStatus,
  lasersOfflineCount,
} from "./robots";
import {
  getActivePlantProfileId,
  isRobotClassifyingByCustomCatCompatPlantProfile,
  isRobotClassifyingByPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  getAlarmsStatus,
  getAlmanacStatus,
  getDiscriminatorStatus,
  getJobStatus,
  getLaserStatus,
  getLifetimeStatus,
  getLocaltimeStatus,
  getModelinatorStatus,
  getP2PStatus,
  getPlantProfileStatus,
  getThinningStatus,
  getVersionStatus,
  getWeedingStatus,
} from "portal/components/robots/RobotStatus";
import {
  getAlmanacPath,
  getCustomerPath,
  getDiscriminatorPath,
  getModelinatorPath,
  getRobotActivePlantProfilePath,
  getRobotHistoryPath,
  getRobotPath,
  RobotSubpath,
} from "./routing";
import { getFormatBooleanForExport } from "portal/components/measurement/formatters";
import { GridColDef } from "@mui/x-data-grid-premium";
import { i18n as I18n, TFunction } from "i18next";
import { isUndefined } from "./identity";
import { MeasurementCell } from "../components/measurement/MeasurementCell";
import { MeasurementSystem } from "./units/units";
import { MetricMeasurement } from "portal/components/measurement/MetricMeasurement";
import { RobotSummaryResponse } from "protos/portal/robots";
import { SummaryCardItemProps } from "portal/components/SummaryCardItem";
import { titleCase, valueOrLoading } from "./strings";
import { values } from "./objects";
import MetricIcon from "@mui/icons-material/ShowChartOutlined";
import React from "react";

export type MetricColumnId = `metric-${Metric["id"]}`;
export enum InfoColumnId {
  GROUP = "group",
  ALARMS = "alarms",
  ALMANAC = "almanac",
  BANDING = "isBanding",
  BANDING_DYNAMIC = "isBandingDynamic",
  BANDING_PROFILE = "banding",
  CUSTOMER = "customer",
  DISCRIMINATOR = "discriminator",
  IMPLEMENTATION_STATUS = "implementationStatus",
  JOB = "job",
  LASERS_OFFLINE = "lasersOffline",
  LIFETIME_TIME = "lifetimeTime",
  LOCAL_TIME = "localTime",
  MODEL = "model",
  P2P = "p2p",
  PLANT_PROFILE = "plantProfile",
  REPORTED_AT = "reportedAt",
  ROBOT = "robot",
  SOFTWARE_VERSION = "softwareVersion",
  TARGET_VERSION = "targetVersion",
  THINNING = "isThinning",
  THINNING_PROFILE = "thinning",
  UPDATE_STATUS = "updateStatus",
  WEEDING = "isWeeding",
}
export type FleetColumnId = InfoColumnId | MetricColumnId;

const metricToFleetColumnId = (metric: Metric): MetricColumnId =>
  `metric-${metric.id}`;

// exported so callers can skip the crop name lookup when a view doesn't
// include the crop column
export const FLEET_CROP_COLUMN_ID = metricToFleetColumnId(METRIC_CROP);

const DEFAULT_COLUMNS: FleetColumnId[] = [
  InfoColumnId.GROUP,
  InfoColumnId.ROBOT,
  InfoColumnId.REPORTED_AT,
  InfoColumnId.CUSTOMER,
  InfoColumnId.LOCAL_TIME,
  InfoColumnId.WEEDING,
  FLEET_CROP_COLUMN_ID,
  InfoColumnId.THINNING,
  InfoColumnId.ALARMS,
  InfoColumnId.LASERS_OFFLINE,
  InfoColumnId.SOFTWARE_VERSION,
  InfoColumnId.TARGET_VERSION,
  InfoColumnId.UPDATE_STATUS,
  InfoColumnId.LIFETIME_TIME,
];

export const isFleetColumnId = (id: string | number): id is string => {
  if (typeof id === "number") {
    return false;
  }
  return [
    ...values(InfoColumnId),
    ...CERTIFIED_METRICS.filter((metric) => isFleetViewMetric(metric)).map(
      (metric) => metricToFleetColumnId(metric)
    ),
  ].includes(id as FleetColumnId);
};

const defaultColumn: Partial<GridColDef> = {
  type: "string",
  sortable: true,
  disableColumnMenu: true,
};

export const getColumns = (
  i18n: I18n,
  t: TFunction,
  measurementSystem: MeasurementSystem,
  useJigsaw: boolean,
  // category collection feature flag; omits the plant profile column when off
  includePlantProfile: boolean
): FleetViewColumn[] => [
  {
    ...defaultColumn,
    field: InfoColumnId.GROUP,
    id: InfoColumnId.GROUP,
    headerName: "",
    valueGetter: (_value, summary) => summary.group,
  },
  {
    ...defaultColumn,
    field: InfoColumnId.ROBOT,
    id: InfoColumnId.ROBOT,
    headerName: titleCase(t("models.robots.robot_one")),
    cellClassName: "font-mono font-bold",
    width: 125,
    valueGetter: (_value, summary) =>
      valueOrLoading(summary.robot?.customerSerial ?? "", t),
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      return serial ? getRobotPath(serial, RobotSubpath.SUMMARY) : undefined;
    },
  },
  {
    ...defaultColumn,
    type: "dateTime",
    field: InfoColumnId.REPORTED_AT,
    id: InfoColumnId.REPORTED_AT,
    headerName: titleCase(t("models.robots.fields.reportedAt")),
    valueGetter: (_value, summary) =>
      summary.robot?.health?.reportedAt
        ? DateTime.fromSeconds(summary.robot.health.reportedAt)
        : undefined,
    valueFormatter: excelFormatter,
    renderCell: getRenderDateTime(i18n, "relative"),
  },
  {
    ...defaultColumn,
    field: InfoColumnId.SOFTWARE_VERSION,
    id: InfoColumnId.SOFTWARE_VERSION,
    headerName: titleCase(t("models.robots.fields.softwareVersion")),
    valueGetter: (_value, summary) => summary.robot?.health?.softwareVersion,
    sortComparator: new Intl.Collator("en-US", { numeric: true }).compare,
    renderForCard: (summary) => getVersionStatus(t, summary),
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      return serial
        ? getRobotPath(serial, RobotSubpath.HARDWARE_VERSIONS)
        : undefined;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.TARGET_VERSION,
    id: InfoColumnId.TARGET_VERSION,
    headerName: titleCase(t("models.robots.fields.targetVersion")),
    valueGetter: (_value, summary) => summary.robot?.health?.targetVersion,
    sortComparator: new Intl.Collator("en-US", { numeric: true }).compare,
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      return serial
        ? getRobotPath(serial, RobotSubpath.HARDWARE_VERSIONS)
        : undefined;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.UPDATE_STATUS,
    id: InfoColumnId.UPDATE_STATUS,
    headerName: titleCase(t("views.fleet.robots.hardware.ready.name")),
    valueGetter: (_value, summary) => {
      const softwareVersion = summary.robot?.health?.softwareVersion;
      const targetVersion = summary.robot?.health?.targetVersion;
      if (isUndefined(softwareVersion) && isUndefined(targetVersion)) {
        return t("utils.descriptors.unknown");
      }
      if (softwareVersion === targetVersion) {
        return t("views.fleet.robots.hardware.ready.values.installed");
      }
      switch (summary.robot?.health?.targetVersionReady) {
        case true: {
          return t("views.fleet.robots.hardware.ready.values.true");
        }
        case false: {
          return t("views.fleet.robots.hardware.ready.values.false");
        }
        default: {
          return t("utils.descriptors.unknown");
        }
      }
    },
  },
  {
    ...defaultColumn,
    type: "boolean",
    field: InfoColumnId.WEEDING,
    id: InfoColumnId.WEEDING,
    headerName: titleCase(t("models.robots.fields.isWeeding")),
    valueGetter: (_value, summary) =>
      Boolean(summary.robot?.health?.fieldConfig?.isWeeding),
    valueFormatter: getFormatBooleanForExport(t),
    renderForCard: (summary, _, robotCropDisplayName) =>
      getWeedingStatus(t, summary, robotCropDisplayName),
  },
  {
    ...defaultColumn,
    type: "boolean",
    field: InfoColumnId.THINNING,
    id: InfoColumnId.THINNING,
    headerName: titleCase(t("models.robots.fields.isThinning")),
    valueGetter: (_value, summary) =>
      Boolean(summary.robot?.health?.fieldConfig?.isThinning),
    valueFormatter: getFormatBooleanForExport(t),
    renderForCard: (summary) => getThinningStatus(t, summary),
  },
  {
    ...defaultColumn,
    field: InfoColumnId.THINNING_PROFILE,
    id: InfoColumnId.THINNING_PROFILE,
    headerName: titleCase(
      t("components.robots.RobotSummary.thinning.withName", {
        name: t("views.fleet.robots.summary.banding.definition"),
      })
    ),
    valueGetter: (_value, summary) => {
      const isThinning = summary.robot?.health?.fieldConfig?.isThinning;
      if (!isThinning) {
        return;
      }
      return summary.robot?.health?.fieldConfig?.activeThinningConfigName;
    },
  },
  {
    ...defaultColumn,
    type: "boolean",
    field: InfoColumnId.BANDING_DYNAMIC,
    id: InfoColumnId.BANDING_DYNAMIC,
    headerName: titleCase(
      t("components.robots.RobotSummary.banding.withName", {
        name: t("views.fleet.robots.summary.banding.dynamic"),
      })
    ),
    valueGetter: (_value, summary) =>
      Boolean(summary.robot?.health?.fieldConfig?.bandingEnabled) &&
      Boolean(summary.robot?.health?.fieldConfig?.bandingDynamic),
    valueFormatter: getFormatBooleanForExport(t),
  },
  {
    ...defaultColumn,
    field: InfoColumnId.BANDING_PROFILE,
    id: InfoColumnId.BANDING_PROFILE,
    headerName: titleCase(
      t("components.robots.RobotSummary.banding.withName", {
        name: t("views.fleet.robots.summary.banding.definition"),
      })
    ),
    valueGetter: (_value, summary) => {
      const isBanding = summary.robot?.health?.fieldConfig?.bandingEnabled;
      if (!isBanding) {
        return;
      }
      return summary.robot?.health?.fieldConfig?.activeBandConfigName;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.ALMANAC,
    id: InfoColumnId.ALMANAC,
    headerName: titleCase(t("models.almanacs.almanac_one")),
    valueGetter: (_value, summary) =>
      isRobotClassifyingByCustomCatCompatPlantProfile(summary)
        ? undefined
        : summary.robot?.health?.fieldConfig?.activeAlmanacName,
    renderForCard: (summary) =>
      isRobotClassifyingByCustomCatCompatPlantProfile(summary)
        ? undefined
        : getAlmanacStatus(t, summary),
    getLink: (summary) => {
      if (isRobotClassifyingByCustomCatCompatPlantProfile(summary)) {
        return;
      }
      const serial = summary.robot?.serial;
      const uuid = summary.robot?.health?.fieldConfig?.activeAlmanacId;
      return serial && uuid ? getAlmanacPath(serial, uuid) : undefined;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.DISCRIMINATOR,
    id: InfoColumnId.DISCRIMINATOR,
    headerName: titleCase(t("models.discriminators.discriminator_one")),
    valueGetter: (_value, summary) =>
      isRobotClassifyingByCustomCatCompatPlantProfile(summary)
        ? undefined
        : summary.robot?.health?.fieldConfig?.activeDiscriminatorName,
    renderForCard: (summary) =>
      isRobotClassifyingByCustomCatCompatPlantProfile(summary)
        ? undefined
        : getDiscriminatorStatus(t, summary),
    getLink: (summary) => {
      if (isRobotClassifyingByCustomCatCompatPlantProfile(summary)) {
        return;
      }
      const serial = summary.robot?.serial;
      const uuid = summary.robot?.health?.fieldConfig?.activeDiscriminatorId;
      return serial && uuid ? getDiscriminatorPath(serial, uuid) : undefined;
    },
  },
  ...(includePlantProfile
    ? [
        {
          ...defaultColumn,
          field: InfoColumnId.PLANT_PROFILE,
          id: InfoColumnId.PLANT_PROFILE,
          headerName: t(
            "models.categoryCollectionProfiles.categoryCollectionProfile_title_one"
          ),
          valueGetter: (_value, summary) => getActivePlantProfileId(summary),
          renderForCard: (summary) =>
            isRobotClassifyingByPlantProfile(summary)
              ? getPlantProfileStatus(t, i18n, summary)
              : undefined,
          getLink: (summary) => {
            const serial = summary.robot?.serial;
            return isRobotClassifyingByPlantProfile(summary) && serial
              ? getRobotActivePlantProfilePath(serial)
              : undefined;
          },
        } satisfies FleetViewColumn,
      ]
    : []),
  {
    ...defaultColumn,
    field: InfoColumnId.MODEL,
    id: InfoColumnId.MODEL,
    headerName: titleCase(t("models.models.model_one")),
    valueGetter: (_value, summary) => summary.robot?.health?.model,
    renderForCard: (summary) => getModelinatorStatus(t, summary),
    cellClassName: "font-mono",
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      const cropId = summary.robot?.health?.cropId;
      const modelId = summary.robot?.health?.fieldConfig?.activeModelinatorId;
      return serial && cropId && modelId
        ? getModelinatorPath(serial, cropId, modelId)
        : undefined;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.P2P,
    id: InfoColumnId.P2P,
    headerName: titleCase(t("models.models.p2p_one")),
    cellClassName: "font-mono",
    valueGetter: (_value, summary) => summary.robot?.health?.model,
    isInternal: true,
    renderForCard: (summary) => getP2PStatus(t, summary),
  },
  {
    ...defaultColumn,
    field: InfoColumnId.JOB,
    id: InfoColumnId.JOB,
    headerName: t("models.jobs.job_title_one"),
    valueGetter: (_value, summary) =>
      summary.robot?.health?.fieldConfig?.activeJobName,
    renderForCard: (summary) => getJobStatus(t, summary),
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      const uuid = summary.robot?.health?.fieldConfig?.activeJobId;
      return serial && uuid ? getRobotHistoryPath(serial, uuid) : undefined;
    },
  },
  {
    ...defaultColumn,
    type: "number",
    field: InfoColumnId.ALARMS,
    id: InfoColumnId.ALARMS,
    headerName: titleCase(t("models.alarms.alarm_other")),
    valueGetter: (_value, summary) =>
      summary.robot?.health?.activeAlarmCount ?? 0,
    renderForCard: (summary) => getAlarmsStatus(t, summary),
    getLink: (summary) => {
      const serial = summary.robot?.serial;
      return serial ? getRobotPath(serial, RobotSubpath.ALARMS) : undefined;
    },
  },
  {
    ...defaultColumn,
    type: "number",
    field: InfoColumnId.LIFETIME_TIME,
    id: InfoColumnId.LIFETIME_TIME,
    headerName: titleCase(t("models.robots.fields.lifetimeTime")),
    valueGetter: (_value, summary) => summary.robot?.health?.robotRuntime240v,
    renderForCard: (summary) =>
      getLifetimeStatus(t, i18n, measurementSystem, summary),
    renderCell: ({ row: summary }) => (
      <MeasurementCell
        value={summary.robot?.health?.robotRuntime240v}
        unit="h"
        cycleSlot={CycleSlot.STATUS_TIME}
      />
    ),
  },
  {
    ...defaultColumn,
    field: InfoColumnId.CUSTOMER,
    id: InfoColumnId.CUSTOMER,
    isInternal: true,
    headerName: titleCase(t("models.customers.customer_one")),
    valueGetter: (_value, summary) => summary.customer?.name,
    getLink: (summary) => {
      const customerUuid = summary.customer?.uuid;
      return customerUuid ? getCustomerPath(customerUuid) : undefined;
    },
  },
  {
    ...defaultColumn,
    field: InfoColumnId.IMPLEMENTATION_STATUS,
    id: InfoColumnId.IMPLEMENTATION_STATUS,
    isInternal: true,
    headerName: titleCase(t("components.RobotImplementationSelector.status")),
    valueGetter: (_value, summary) =>
      formatImplementationStatus(t, summary.robot?.implementationStatus),
  },
  {
    ...defaultColumn,
    type: "dateTime",
    field: InfoColumnId.LOCAL_TIME,
    id: InfoColumnId.LOCAL_TIME,
    headerName: titleCase(t("models.robots.fields.localTime")),
    valueGetter: (_value, summary) => {
      const timezone = summary.robot?.health?.timezone;
      return timezone ? DateTime.local().setZone(timezone) : DateTime.local();
    },
    valueFormatter: excelFormatter,
    renderCell: getRenderDateTime(i18n, "localtime"),
    renderForCard: (summary) => getLocaltimeStatus(t, i18n, summary),
  },
  {
    ...defaultColumn,
    type: "number",
    field: InfoColumnId.LASERS_OFFLINE,
    id: InfoColumnId.LASERS_OFFLINE,
    headerName: titleCase(t("models.robots.fields.lasersOffline")),
    valueGetter: (_value, summary) =>
      summary.lasers ? lasersOfflineCount(summary.lasers) : undefined,
    renderForCard: (summary, config) =>
      getLaserStatus(t, summary.robot?.serial, config, summary, false),
  },
  ...getFleetViewMetrics(useJigsaw).flatMap((metric) => {
    const id = metricToFleetColumnId(metric);

    const metricName = getMetricName(t, metric);

    const column: FleetViewColumn = {
      ...defaultColumn,
      field: id,
      id,
      metric,
      type: metric.type,
      headerName: metricName,
      valueGetter: (_value, summary) => {
        const dailyMetrics =
          summary.dailyMetrics ?? DailyMetricResponse.fromPartial({});
        const rawValue = dailyMetrics[metric.id];
        if (metric.type === "boolean") {
          return Boolean(rawValue);
        }
        return metric.getValue(dailyMetrics);
      },
      isInternal: metric.validation === MetricValidation.INTERNAL,
      isPending: metric.validation === MetricValidation.PENDING,
      renderForCard: (summary) => {
        return {
          icon: <MetricIcon />,
          text: (
            <div className="flex items-center justify-start gap-1">
              {metricName}:{" "}
              <MetricMeasurement data={summary.dailyMetrics} metric={metric} />
            </div>
          ),
        };
      },
    };
    if (metric.type === "boolean") {
      column.valueFormatter = getFormatBooleanForExport(t);
    } else {
      column.renderCell = ({ value }) => (
        <MeasurementCell
          cycleSlot={metric.cycleSlot}
          unit={"units" in metric ? metric.units : undefined}
          value={value}
        />
      );
    }
    return [column];
  }),
];

export interface GroupedRobotSummaryResponse extends RobotSummaryResponse {
  group: string;
}

/** Fleet columns only use the (value, row) params of MUI's GridValueGetter. */
export type FleetColumnValueGetter = (
  value: unknown,
  summary: GroupedRobotSummaryResponse
) => unknown;

export type FleetViewColumn = Omit<
  GridColDef<GroupedRobotSummaryResponse>,
  "valueGetter"
> & {
  id: string;
  valueGetter?: FleetColumnValueGetter;
  isPending?: boolean;
  isInternal?: boolean;
  metric?: Metric;
  renderForCard?: (
    summary: RobotSummaryResponse,
    config: ConfigNode | undefined,
    robotCropDisplayName: string | undefined
  ) => SummaryCardItemProps | undefined;
  getLink?: (summary: RobotSummaryResponse) => string | undefined;
};

export const getFleetColumnSearchValue = (
  summary: GroupedRobotSummaryResponse,
  column: FleetViewColumn
): string => {
  if (!column.valueGetter) {
    return "";
  }
  return String(column.valueGetter(undefined, summary) ?? "");
};

const DEFAULT_STATUSES_CUSTOMER = [
  ImplementationStatus.ACTIVE,
  ImplementationStatus.INACTIVE,
  ImplementationStatus.OFF_SEASON,
  ImplementationStatus.WINTERIZED,
];

const DEFAULT_STATUSES_INTERNAL = [
  ...DEFAULT_STATUSES_CUSTOMER,
  ImplementationStatus.IMPLEMENTATION,
];

export const generateDefaultFleetView = (
  t: TFunction,
  user?: CarbonUser
): FleetView => {
  const viewMode = user?.isInternal
    ? viewModeFromJSON(ViewMode.VIEW_MODE_TABLE)
    : viewModeFromJSON(ViewMode.VIEW_MODE_CARDS);
  const showMap = viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS);
  return FleetView.fromPartial({
    columns: DEFAULT_COLUMNS,
    name: t("views.reports.tools.robotsLabel.all"),
    showInternal: true,
    showOffline: true,
    showMap,
    statuses: user?.isInternal
      ? DEFAULT_STATUSES_INTERNAL
      : DEFAULT_STATUSES_CUSTOMER,
    viewMode,
  });
};

export const getDefaultFleetView = (
  fleetViews: Record<number, FleetView>,
  user: CarbonUser | undefined
): FleetView | undefined => {
  const defaultFleetViewId = user?.userMetadata?.defaultFleetViewId;
  if (defaultFleetViewId) {
    return fleetViews[defaultFleetViewId];
  }
  const views = values(fleetViews);
  if (views.length > 0) {
    return views[0];
  }
};
